import { Dayjs } from "dayjs";
import {
  Behavior,
  DEFAULT_TERMINATOR_BEHAVIOR,
  TerminatorBehavior,
} from "./algoSpec";
import { Direction } from "../lazertrader/types";
import { ProductType } from "../../Services/nats/contracts";
import { FormikState } from "formik";
import { BalancingGroup, DEFAULT_BALANCING_GROUP } from "./balancingGroup";

export type ConfigTabState = {
  algo_id?: string;
  product_definition: {
    product_name: string;
    delivery_start: Dayjs | undefined;
    delivery_end: Dayjs | undefined;
    display_grid: string;
    grid: string;
    /**
     * The delivery areas `grid` may be switched to. More than one when the
     * clicked price came from an order book shared by coupled areas, which makes
     * the area ambiguous. Empty when the grid is not the trader's to choose —
     * editing an existing algo.
     */
    grid_options: string[];
    prefilled_product_types: ProductType[];
  };

  direction?: Direction;
  price?: number | null;
  quantity?: number | null;
  behavior?: Behavior;
  /**
   * Terminator only, sent as `configuration.behaviors`. A new form starts on
   * {@link DEFAULT_TERMINATOR_BEHAVIOR}. `undefined` only when editing a
   * terminator whose behaviour this UI does not offer — the trader then has to
   * pick one, and Submit stays disabled until they do.
   */
  terminator_behavior?: TerminatorBehavior;
  margin_override?: number | null;
  reference_price?: number | null;
  /** LinearCloser only. Strings at runtime, like the other price fields. */
  min_margin?: number | null;
  max_margin?: number | null;
  upper_omt_update_frequency?: number | null;

  tag?: string;

  /**
   * New algos only. Every new form starts on {@link DEFAULT_BALANCING_GROUP}
   * via `initialState`, so a submit or cancel puts it back. When editing it
   * holds the algo's own group for display only — the field is disabled and an
   * update never reads it (see `withEchoedBalancingGroup`).
   */
  balancing_group?: BalancingGroup;

  // used to calculate quantity updates when algo has traded
  traded_quantity?: number;
  display_quantity?: number | null;

  // used to indicate a prefill update
  prefill_id?: string;

  // algo runtime
  start?: Dayjs;
  end?: Dayjs;
};

export const initialState: ConfigTabState = {
  product_definition: {
    product_name: "",
    delivery_start: undefined,
    delivery_end: undefined,
    display_grid: "",
    grid: "",
    grid_options: [],
    prefilled_product_types: [],
  },

  direction: Direction.SELL,
  behavior: Behavior.LIMIT_ORDER,
  terminator_behavior: DEFAULT_TERMINATOR_BEHAVIOR,
  balancing_group: DEFAULT_BALANCING_GROUP,
};

/** A terminator set to {@link TerminatorBehavior.LINEAR_CLOSER}. */
export const isLinearCloser = (
  values: Pick<ConfigTabState, "behavior" | "terminator_behavior">
): boolean =>
  values.behavior === Behavior.TERMINATOR &&
  values.terminator_behavior === TerminatorBehavior.LINEAR_CLOSER;

/**
 * StepShimurai and LinearCloser: the algos that price off a reference, which
 * an order book click starts at the selected grid's day-ahead spot.
 */
export const takesSpotReference = (
  values: Pick<ConfigTabState, "behavior" | "terminator_behavior">
): boolean =>
  values.behavior === Behavior.STEP_SHIMURAI || isLinearCloser(values);

const customResetFn =(initialValues: ConfigTabState) => {
  return (
    behavior: Behavior | undefined,
    resetForm: (values: Partial<FormikState<ConfigTabState>>) => void
  ) => {
    // Carrying the behaviour over keeps the trader on the form they were using.
    // The fallback matters for the shimurai variants this UI cannot create,
    // whose `algo_spec` maps to no `Behavior` at all: without it, editing one
    // would leave the form with no behaviour set, and the *next* new algo would
    // submit an undefined `algo_spec.name`.
    resetForm({
      values: { ...initialValues, behavior: behavior ?? initialValues.behavior },
    });
  };
};
export const customResetForm = customResetFn(initialState);
